/* eslint-disable no-param-reassign */
import {
  AsyncThunk, createSlice, Dispatch, PayloadAction,
} from '@reduxjs/toolkit';
import { Content, ContentStatus } from '../../common/models/content/Content';
import { FormAnswer } from '../../common/models/FormAnswer';
import type { RootState } from '../storeSetup';
import {
  getContent,
  getFilteredContent,
  getPersonalizedContent,
  getContentFormAnswers,
  getContentStatus,
  getFollowTopics,
  getTopTopics,
  addFollowTopic,
  deleteFollowTopic,
  getContentByTopTrade,
  getVideoContent,
  getArticleContent,
  getPodcastContent,
  getRewardsContent,
  getTopic2Content,
  getTopic3Content,
  getTopic4Content,
  getTopic5Content,
  getTopic1Content,
  ResponseError,
  getTopTradeContentStatus,
  getVideoContentStatus,
  getArticleContentStatus,
  getPodcastContentStatus,
  getRewardsContentStatus,
  getTopic1ContentStatus,
  getTopic2ContentStatus,
  getTopic3ContentStatus,
  getTopic4ContentStatus,
  getTopic5ContentStatus,
  getFilteredContentStatus,
} from '../thunks/contentThunk';
import { FollowTopic } from '../../common/models/FollowTopic';
import { TopicsCount } from '../../common/models/content/TopicsCount';
import { GetContentRequestV4 } from '../../common/models/content/GetContentRequest';

interface ContentState {
  contentList: Content[];
  filteredContentList: Content[],
  topTradeContentList: Content[];
  videoContentList: Content[];
  articleContentList: Content[];
  podcastContentList: Content[];
  rewardsContentList: Content[];
  topic1ContentList: Content[];
  topic2ContentList: Content[];
  topic3ContentList: Content[];
  topic4ContentList: Content[];
  topic5ContentList: Content[];
  getContentListState: 'idle' | 'pending';
  getFilteredContentState: 'idle' | 'pending';
  getTopTradeContentState: 'idle' | 'pending';
  getVideoContentState: 'idle' | 'pending';
  getArticleContentState: 'idle' | 'pending';
  getPodcastContentState: 'idle' | 'pending';
  getRewardsContentState: 'idle' | 'pending';
  getTopic1ContentState: 'idle' | 'pending';
  getTopic2ContentState: 'idle' | 'pending';
  getTopic3ContentState: 'idle' | 'pending';
  getTopic4ContentState: 'idle' | 'pending';
  getTopic5ContentState: 'idle' | 'pending';
  getFollowTopicsState: 'idle' | 'pending';
  getTopTopicsState: 'idle' | 'pending';
  addFollowTopicState: 'idle' | 'pending';
  deleteFollowTopicState: 'idle' | 'pending';
  surveyFormAnswers: FormAnswer[];
  pageNumber: number;
  hasMoreContent: boolean;
  topTradePageNumber: number;
  topTradeHasMoreContent: boolean;
  podcastPageNumber: number;
  podcastHasMoreContent: boolean;
  videoPageNumber: number;
  videoHasMoreContent: boolean;
  rewardsPageNumber: number;
  rewardsHasMoreContent: boolean;
  topic1PageNumber: number;
  topic2PageNumber: number;
  topic3PageNumber: number;
  topic4PageNumber: number;
  topic5PageNumber: number;
  filteredContentPageNumber: number;
  topic1HasMoreContent: boolean;
  topic2HasMoreContent: boolean;
  topic3HasMoreContent: boolean;
  topic4HasMoreContent: boolean;
  topic5HasMoreContent: boolean;
  hasMoreFilteredContent: boolean;
  isContentPreview: boolean;
  previewContentId: number;
  searchHistory: string[];
  selectedTopicIndex: number;
  followTopicsCardViewCount: number;
  behrPromotionCardViewCount: number;
  followTopics: FollowTopic[]
  topTopics: TopicsCount[]
}

// Define the initial state using that type
const initialState: ContentState = {
  contentList: [],
  filteredContentList: [],
  topTradeContentList: [],
  videoContentList: [],
  articleContentList: [],
  podcastContentList: [],
  rewardsContentList: [],
  topic1ContentList: [],
  topic2ContentList: [],
  topic3ContentList: [],
  topic4ContentList: [],
  topic5ContentList: [],
  getContentListState: 'pending',
  getFilteredContentState: 'pending',
  getTopTradeContentState: 'pending',
  getVideoContentState: 'pending',
  getArticleContentState: 'pending',
  getPodcastContentState: 'pending',
  getRewardsContentState: 'pending',
  getTopic1ContentState: 'pending',
  getTopic2ContentState: 'pending',
  getTopic3ContentState: 'pending',
  getTopic4ContentState: 'pending',
  getTopic5ContentState: 'pending',
  getFollowTopicsState: 'pending',
  getTopTopicsState: 'pending',
  addFollowTopicState: 'idle',
  deleteFollowTopicState: 'idle',
  surveyFormAnswers: [],
  pageNumber: 1,
  hasMoreContent: false,
  topTradePageNumber: 1,
  topTradeHasMoreContent: false,
  podcastPageNumber: 1,
  podcastHasMoreContent: false,
  videoPageNumber: 1,
  videoHasMoreContent: false,
  rewardsPageNumber: 1,
  rewardsHasMoreContent: false,
  topic1PageNumber: 1,
  topic2PageNumber: 1,
  topic3PageNumber: 1,
  topic4PageNumber: 1,
  topic5PageNumber: 1,
  filteredContentPageNumber: 1,
  topic1HasMoreContent: false,
  topic2HasMoreContent: false,
  topic3HasMoreContent: false,
  topic4HasMoreContent: false,
  topic5HasMoreContent: false,
  hasMoreFilteredContent: false,
  isContentPreview: false,
  previewContentId: 0,
  searchHistory: [],
  selectedTopicIndex: -1,
  followTopicsCardViewCount: 0,
  behrPromotionCardViewCount: 0,
  followTopics: [],
  topTopics: [],
};

export const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    ResetContentList: (state, action: PayloadAction<Content[]>) => {
      state.contentList = action.payload;
    },
    ResetFilteredContentList: (state, action: PayloadAction<Content[]>) => {
      state.filteredContentList = action.payload;
    },
    ResetTopTradeContentList: (state, action: PayloadAction<Content[]>) => {
      state.topTradeContentList = action.payload;
    },
    ResetVideoContentList: (state, action: PayloadAction<Content[]>) => {
      state.videoContentList = action.payload;
    },
    ResetArticleContentList: (state, action: PayloadAction<Content[]>) => {
      state.articleContentList = action.payload;
    },
    ResetPodcastContentList: (state, action: PayloadAction<Content[]>) => {
      state.podcastContentList = action.payload;
    },
    ResetRewardsContentList: (state, action: PayloadAction<Content[]>) => {
      state.rewardsContentList = action.payload;
    },
    ResetTopic1ContentList: (state, action: PayloadAction<Content[]>) => {
      state.topic1ContentList = action.payload;
    },
    ResetTopic2ContentList: (state, action: PayloadAction<Content[]>) => {
      state.topic2ContentList = action.payload;
    },
    ResetTopic3ContentList: (state, action: PayloadAction<Content[]>) => {
      state.topic3ContentList = action.payload;
    },
    ResetTopic4ContentList: (state, action: PayloadAction<Content[]>) => {
      state.topic4ContentList = action.payload;
    },
    ResetTopic5ContentList: (state, action: PayloadAction<Content[]>) => {
      state.topic5ContentList = action.payload;
    },
    UpdatePageNumber: (state, action: PayloadAction<number>) => {
      state.pageNumber = action.payload;
    },
    UpdatePodcastPageNumber: (state, action: PayloadAction<number>) => {
      state.podcastPageNumber = action.payload;
    },
    UpdateVideoPageNumber: (state, action: PayloadAction<number>) => {
      state.videoPageNumber = action.payload;
    },
    UpdateRewardsPageNumber: (state, action: PayloadAction<number>) => {
      state.rewardsPageNumber = action.payload;
    },
    UpdateTopic1PageNumber: (state, action: PayloadAction<number>) => {
      state.topic1PageNumber = action.payload;
    },
    UpdateTopic2PageNumber: (state, action: PayloadAction<number>) => {
      state.topic2PageNumber = action.payload;
    },
    UpdateTopic3PageNumber: (state, action: PayloadAction<number>) => {
      state.topic3PageNumber = action.payload;
    },
    UpdateTopic4PageNumber: (state, action: PayloadAction<number>) => {
      state.topic4PageNumber = action.payload;
    },
    UpdateTopic5PageNumber: (state, action: PayloadAction<number>) => {
      state.topic5PageNumber = action.payload;
    },
    UpdateFilteredContentPageNumber: (state, action: PayloadAction<number>) => {
      state.filteredContentPageNumber = action.payload;
    },
    UpdateTopTradePageNumber: (state, action: PayloadAction<number>) => {
      state.topTradePageNumber = action.payload;
    },
    UpdateFollowTopicsCardViewCount: (state, action: PayloadAction<number>) => {
      state.followTopicsCardViewCount = action.payload;
    },
    UpdateBehrPromotionCardViewCount: (state, action: PayloadAction<number>) => {
      state.behrPromotionCardViewCount = action.payload;
    },
    UpdateHasMoreContent: (state, action: PayloadAction<boolean>) => {
      state.hasMoreContent = action.payload;
    },
    UpdateHasMoreFilteredContent: (state, action: PayloadAction<boolean>) => {
      state.hasMoreFilteredContent = action.payload;
    },
    UpdatePodcastHasMoreContent: (state, action: PayloadAction<boolean>) => {
      state.podcastHasMoreContent = action.payload;
    },
    UpdateVideoHasMoreContent: (state, action: PayloadAction<boolean>) => {
      state.videoHasMoreContent = action.payload;
    },
    UpdateRewardsHasMoreContent: (state, action: PayloadAction<boolean>) => {
      state.rewardsHasMoreContent = action.payload;
    },
    UpdateTopic1HasMoreContent: (state, action: PayloadAction<boolean>) => {
      state.topic1HasMoreContent = action.payload;
    },
    UpdateTopic2HasMoreContent: (state, action: PayloadAction<boolean>) => {
      state.topic2HasMoreContent = action.payload;
    },
    UpdateTopic3HasMoreContent: (state, action: PayloadAction<boolean>) => {
      state.topic3HasMoreContent = action.payload;
    },
    UpdateTopic4HasMoreContent: (state, action: PayloadAction<boolean>) => {
      state.topic4HasMoreContent = action.payload;
    },
    UpdateTopic5HasMoreContent: (state, action: PayloadAction<boolean>) => {
      state.topic5HasMoreContent = action.payload;
    },
    UpdateTopTradeHasMoreContent: (state, action: PayloadAction<boolean>) => {
      state.topTradeHasMoreContent = action.payload;
    },
    removePollAnswer: (state, action: PayloadAction<string>) => {
      state.surveyFormAnswers =
        state.surveyFormAnswers.filter((item) => item.id !== action.payload);
    },
    UpdateIsContentPreview: (state, action: PayloadAction<boolean>) => {
      state.isContentPreview = action.payload;
    },
    UpdatePreviewContentId: (state, action: PayloadAction<number>) => {
      state.previewContentId = action.payload;
    },
    PushSearchHistory: (state, action: PayloadAction<string>) => {
      if (action.payload && !state.searchHistory.includes(action.payload)) {
        // Keep latest 5 items
        state.searchHistory = [action.payload].concat(state.searchHistory.slice(0, 4));
      }
    },
    UpdateSelectedTopicIndex: (state, action: PayloadAction<number>) => {
      state.selectedTopicIndex = action.payload;
    },
  },
  extraReducers(builder) {
    // helper function to add cases for the different content categories
    function addContentCategoryCase(
      contentListKey: keyof typeof initialState,
      getContentStateKey: keyof typeof initialState,
      contentCategoryThunk: AsyncThunk<Content[], GetContentRequestV4, {
        rejectValue: ResponseError;
        state: RootState;
        dispatch?: Dispatch | undefined;
        extra?: unknown;
        serializedErrorType?: unknown;
        pendingMeta?: unknown;
        fulfilledMeta?: unknown;
        rejectedMeta?: unknown;
      }>,
      contentCategoryStatusThunk: AsyncThunk<ContentStatus, string, {
        rejectValue: ResponseError;
        state?: unknown;
        dispatch?: Dispatch | undefined;
        extra?: unknown;
        serializedErrorType?: unknown;
        pendingMeta?: unknown;
        fulfilledMeta?: unknown;
        rejectedMeta?: unknown;
      }>,
    ) {
      builder
        .addCase(contentCategoryThunk.pending, (state) => {
          (state[getContentStateKey] as 'idle' | 'pending') = 'pending';
        })
        .addCase(contentCategoryThunk.fulfilled, (state, action) => {
          const currentList = state[contentListKey] as Content[];
          const updatedList = [...currentList, ...action.payload];
          (state[contentListKey] as Content[]) = updatedList.filter((value, index, self) => (
            index === self.findIndex((t) => (t.id === value.id))
          )) as Content[];
          (state[getContentStateKey] as 'idle' | 'pending') = 'idle';
        })
        .addCase(contentCategoryThunk.rejected, (state) => {
          (state[getContentStateKey] as 'idle' | 'pending') = 'idle';
        })
        .addCase(
          contentCategoryStatusThunk.fulfilled,
          (state, action: PayloadAction<ContentStatus>) => {
            (state[contentListKey] as Content[]) = (state[contentListKey] as Content[])
              .map((content) => {
                const likeStatus = action.payload[`${content.id}`];
                return likeStatus ? { ...content, likeStatus } : content;
              });
          },
        );
    }

    // start building cases
    builder
      .addCase(getPersonalizedContent.pending, (state, action) => {
        state.getContentListState = 'pending';
      })
      .addCase(getPersonalizedContent.fulfilled, (state, action) => {
        state.contentList = action.payload;
        state.getContentListState = 'idle';
      })
      .addCase(getPersonalizedContent.rejected, (state, action) => {
        state.getContentListState = 'idle';
      })
      .addCase(getContent.pending, (state, action) => {
        state.getContentListState = 'pending';
      })
      .addCase(getContent.fulfilled, (state, action) => {
        const newContentList = [...state.contentList, ...action.payload];
        state.contentList = newContentList.filter((value, index, self) => (
          index === self.findIndex((t) => (t.id === value.id))
        ));
        state.getContentListState = 'idle';
      })
      .addCase(getContent.rejected, (state, action) => {
        state.getContentListState = 'idle';
      })
      .addCase(getContentStatus.fulfilled, (state, action: PayloadAction<ContentStatus>) => {
        state.contentList = state.contentList.map((content) => {
          const likeStatus = action.payload[`${content.id}`];
          return likeStatus ? { ...content, likeStatus } : content;
        });
      });

    // Add cases for different content categories
    addContentCategoryCase('topTradeContentList', 'getTopTradeContentState', getContentByTopTrade, getTopTradeContentStatus);
    addContentCategoryCase('videoContentList', 'getVideoContentState', getVideoContent, getVideoContentStatus);
    addContentCategoryCase('articleContentList', 'getArticleContentState', getArticleContent, getArticleContentStatus);
    addContentCategoryCase('podcastContentList', 'getPodcastContentState', getPodcastContent, getPodcastContentStatus);
    addContentCategoryCase('rewardsContentList', 'getRewardsContentState', getRewardsContent, getRewardsContentStatus);
    addContentCategoryCase('topic1ContentList', 'getTopic1ContentState', getTopic1Content, getTopic1ContentStatus);
    addContentCategoryCase('topic2ContentList', 'getTopic2ContentState', getTopic2Content, getTopic2ContentStatus);
    addContentCategoryCase('topic3ContentList', 'getTopic3ContentState', getTopic3Content, getTopic3ContentStatus);
    addContentCategoryCase('topic4ContentList', 'getTopic4ContentState', getTopic4Content, getTopic4ContentStatus);
    addContentCategoryCase('topic5ContentList', 'getTopic5ContentState', getTopic5Content, getTopic5ContentStatus);
    addContentCategoryCase('filteredContentList', 'getFilteredContentState', getFilteredContent, getFilteredContentStatus);

    // continue adding cases
    builder
      .addCase(getContentFormAnswers.fulfilled, (state, action) => {
        const newSurveyFormAnswers = [...state.surveyFormAnswers, ...action.payload];
        state.surveyFormAnswers = newSurveyFormAnswers.filter((value, index, self) => (
          index === self.findIndex((t) => (t.id === value.id))
        ));
      })
      .addCase(getFollowTopics.pending, (state, action) => {
        state.getFollowTopicsState = 'pending';
      })
      .addCase(getFollowTopics.fulfilled, (state, action) => {
        state.followTopics = action.payload;
        state.getFollowTopicsState = 'idle';
      })
      .addCase(getFollowTopics.rejected, (state, action) => {
        state.getFollowTopicsState = 'idle';
      })
      .addCase(addFollowTopic.pending, (state) => {
        state.addFollowTopicState = 'pending';
      })
      .addCase(addFollowTopic.fulfilled, (state, action) => {
        const newFollowTopics = action.payload;
        state.followTopics = newFollowTopics;
        state.addFollowTopicState = 'idle';
      })
      .addCase(addFollowTopic.rejected, (state, action) => {
        state.addFollowTopicState = 'idle';
      })
      .addCase(deleteFollowTopic.pending, (state) => {
        state.deleteFollowTopicState = 'pending';
      })
      .addCase(deleteFollowTopic.fulfilled, (state, action) => {
        const newFollowTopics = action.payload;
        state.followTopics = newFollowTopics;
        state.deleteFollowTopicState = 'idle';
      })
      .addCase(deleteFollowTopic.rejected, (state, action) => {
        state.deleteFollowTopicState = 'idle';
      })
      .addCase(getTopTopics.pending, (state, action) => {
        state.getTopTopicsState = 'pending';
      })
      .addCase(getTopTopics.fulfilled, (state, action) => {
        state.topTopics = action.payload;
        state.getTopTopicsState = 'idle';
      })
      .addCase(getTopTopics.rejected, (state, action) => {
        state.getTopTopicsState = 'idle';
      });
  },
});

export const {
  ResetContentList,
  ResetFilteredContentList,
  ResetTopTradeContentList,
  ResetVideoContentList,
  ResetArticleContentList,
  ResetPodcastContentList,
  ResetRewardsContentList,
  ResetTopic1ContentList,
  ResetTopic2ContentList,
  ResetTopic3ContentList,
  ResetTopic4ContentList,
  ResetTopic5ContentList,
  UpdatePageNumber,
  UpdateHasMoreContent,
  UpdateTopTradePageNumber,
  UpdateTopTradeHasMoreContent,
  UpdatePodcastPageNumber,
  UpdatePodcastHasMoreContent,
  UpdateVideoPageNumber,
  UpdateRewardsPageNumber,
  UpdateVideoHasMoreContent,
  UpdateRewardsHasMoreContent,
  UpdateTopic1PageNumber,
  UpdateTopic2PageNumber,
  UpdateTopic3PageNumber,
  UpdateTopic4PageNumber,
  UpdateTopic5PageNumber,
  UpdateTopic1HasMoreContent,
  UpdateTopic2HasMoreContent,
  UpdateTopic3HasMoreContent,
  UpdateTopic4HasMoreContent,
  UpdateTopic5HasMoreContent,
  removePollAnswer,
  UpdateIsContentPreview,
  UpdatePreviewContentId,
  PushSearchHistory,
  UpdateSelectedTopicIndex,
  UpdateFollowTopicsCardViewCount,
  UpdateBehrPromotionCardViewCount,
  UpdateHasMoreFilteredContent,
  UpdateFilteredContentPageNumber,
} = contentSlice.actions;

export const selectContentList =
  (state: RootState) => state.content.contentList;
export const selectFilteredContentList =
  (state: RootState) => state.content.filteredContentList;
export const selectGetContentListState =
  (state: RootState) => state.content.getContentListState;
export const selectGetFilteredContentListState =
  (state: RootState) => state.content.getFilteredContentState;
export const selectVideoContentList =
  (state: RootState) => state.content.videoContentList;
export const selectGetVideoContentState =
  (state: RootState) => state.content.getVideoContentState;
export const selectTopTradeContentList =
  (state: RootState) => state.content.topTradeContentList;
export const selectGetTopTradeContentState =
  (state: RootState) => state.content.getTopTradeContentState;
export const selectArticleContentList =
  (state: RootState) => state.content.articleContentList;
export const selectGetArticleContentState =
  (state: RootState) => state.content.getArticleContentState;
export const selectPodcastContentList =
  (state: RootState) => state.content.podcastContentList;
export const selectGetPodcastContentState =
  (state: RootState) => state.content.getPodcastContentState;
export const selectRewardsContentList =
  (state: RootState) => state.content.rewardsContentList;
export const selectGetRewardsContentState =
  (state: RootState) => state.content.getRewardsContentState;
export const selectTopic1ContentList =
  (state: RootState) => state.content.topic1ContentList;
export const selectGetTopic1ContentState =
  (state: RootState) => state.content.getTopic1ContentState;
export const selectTopic2ContentList =
  (state: RootState) => state.content.topic2ContentList;
export const selectGetTopic2ContentState =
  (state: RootState) => state.content.getTopic2ContentState;
export const selectTopic3ContentList =
  (state: RootState) => state.content.topic3ContentList;
export const selectGetTopic3ContentState =
  (state: RootState) => state.content.getTopic3ContentState;
export const selectTopic5ContentList =
  (state: RootState) => state.content.topic5ContentList;
export const selectGetTopic5ContentState =
  (state: RootState) => state.content.getTopic5ContentState;
export const selectTopic4ContentList =
  (state: RootState) => state.content.topic4ContentList;
export const selectGetTopic4ContentState =
  (state: RootState) => state.content.getTopic4ContentState;
export const selectSurveyFormAnswers = (state: RootState) => state.content.surveyFormAnswers;
export const selectContentPageNumber = (state: RootState) => state.content.pageNumber;
export const selectHasMoreContent = (state: RootState) => state.content.hasMoreContent;
export const selectTopTradeContentPageNumber =
  (state: RootState) => state.content.topTradePageNumber;
export const selectTopTradeHasMoreContent =
  (state: RootState) => state.content.topTradeHasMoreContent;
export const selectPodcastContentPageNumber = (state: RootState) => state.content.podcastPageNumber;
export const selectPodcastHasMoreContent =
  (state: RootState) => state.content.podcastHasMoreContent;
export const selectVideoContentPageNumber = (state: RootState) => state.content.videoPageNumber;
export const selectRewardsContentPageNumber = (state: RootState) => state.content.rewardsPageNumber;
export const selectVideoHasMoreContent = (state: RootState) => state.content.videoHasMoreContent;
export const selectHasMoreFilteredContent =
  (state: RootState) => state.content.hasMoreFilteredContent;
export const selectRewardsHasMoreContent =
  (state: RootState) => state.content.rewardsHasMoreContent;
export const selectTopic1ContentPageNumber = (state: RootState) => state.content.topic1PageNumber;
export const selectTopic2ContentPageNumber = (state: RootState) => state.content.topic2PageNumber;
export const selectTopic3ContentPageNumber = (state: RootState) => state.content.topic3PageNumber;
export const selectTopic4ContentPageNumber = (state: RootState) => state.content.topic4PageNumber;
export const selectTopic5ContentPageNumber = (state: RootState) => state.content.topic5PageNumber;
export const selectFilteredContentPageNumber =
  (state: RootState) => state.content.filteredContentPageNumber;
export const selectTopic1HasMoreContent = (state: RootState) => state.content.topic1HasMoreContent;
export const selectTopic2HasMoreContent = (state: RootState) => state.content.topic2HasMoreContent;
export const selectTopic3HasMoreContent = (state: RootState) => state.content.topic3HasMoreContent;
export const selectTopic4HasMoreContent = (state: RootState) => state.content.topic4HasMoreContent;
export const selectTopic5HasMoreContent = (state: RootState) => state.content.topic5HasMoreContent;
export const selectIsContentPreview = (state: RootState) => state.content.isContentPreview;
export const selectPreviewContentId = (state: RootState) => state.content.previewContentId;
export const selectSearchHistory = (state: RootState) => state.content.searchHistory;
export const selectSelectedTopicIndex = (state: RootState) => state.content.selectedTopicIndex;
export const selectFollowTopicsCardViewCount =
  (state: RootState) => state.content.followTopicsCardViewCount;
export const selectBehrPromotionCardViewCount =
  (state: RootState) => state.content.behrPromotionCardViewCount;
export const selectGetFollowTopicsState = (state: RootState) => state.content.getFollowTopicsState;
export const selectGetTopTopicsState = (state: RootState) => state.content.getTopTopicsState;
export const selectFollowTopics = (state: RootState) => state.content.followTopics;
export const selectTopTopics = (state: RootState) => state.content.topTopics;

export default contentSlice.reducer;
